import { bookingGroupStatus, byRowId, groupRowsByBooking, type BookingRows } from "@bednbody/logic";
import {
  AdminDataTable,
  AdminTableIdentity,
  AdminTableRowMenu,
} from "@bednbody/ui/components/admin-data-table";
import { Badge } from "@bednbody/ui/components/badge";
import { cn } from "@bednbody/ui/lib/utils";
import type { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import type { AttentionKind, LiveAttentionItem } from "@/features/live-map/live-map.attention";
import { minutesSince } from "@/lib/time";
import type { CommandBooking } from "../command-center.hooks";
import { PRE_SESSION_STATUSES } from "../command-center.hooks";

type Group = BookingRows<CommandBooking>;

function namesOf(names: (string | null | undefined)[]): string {
  const unique = [...new Set(names.filter((name): name is string => Boolean(name)))];
  return unique.length > 0 ? unique.join(", ") : "—";
}

function statusOf(group: Group): string {
  return bookingGroupStatus(group.map((booking) => booking.status));
}

// BL-20 — the Attention column. Its flags come from the same engine as the
// dashboard strip and the live map (`buildLiveAttention`), so a row is flagged
// here exactly when the map would list it.
const FLAG_LABEL: Record<AttentionKind, string> = {
  payment: "Payment",
  no_show: "No-show",
  disconnected: "Lost connection",
  late: "Late",
  no_rider: "No rider",
  cash: "Cash",
  kyc: "ID",
  return_wait: "Waiting to go home",
};

interface Flag {
  id: string;
  label: string;
  tone: "danger" | "warning";
}

interface LiveOpsTableProps {
  bookings: CommandBooking[];
  isLoading: boolean;
  /** Attention items by booking id. Omit and the column is not drawn. */
  attentionByBookingId?: ReadonlyMap<string, readonly LiveAttentionItem[]>;
  /** Booking refs named by an SOS not yet resolved. */
  sosRefs?: ReadonlySet<string>;
  emptyDescription?: string;
  exportFilename?: string;
}

/** Company-wide live feed. This component is never rendered for hub managers. */
export function LiveOpsTable({
  bookings,
  isLoading,
  attentionByBookingId,
  sosRefs,
  emptyDescription = "New bookings will appear here in real time.",
  exportFilename = "live-operations",
}: LiveOpsTableProps) {
  const groups = useMemo(
    () => groupRowsByBooking(bookings, (booking) => booking.booking_group_id, byRowId),
    [bookings],
  );
  const showAttention = attentionByBookingId !== undefined;

  /** SOS first, then the engine's items; one flag per kind per group. */
  function flagsOf(group: Group): Flag[] {
    const flags: Flag[] = [];
    if (group.some((booking) => sosRefs?.has(booking.ref))) {
      flags.push({ id: "sos", label: "SOS", tone: "danger" });
    }
    const seen = new Set<AttentionKind>();
    for (const booking of group) {
      for (const item of attentionByBookingId?.get(booking.id) ?? []) {
        if (seen.has(item.kind)) continue;
        seen.add(item.kind);
        flags.push({
          id: item.kind,
          label:
            item.kind === "late"
              ? `${FLAG_LABEL.late} · ${minutesSince(booking.scheduled_at)}m`
              : FLAG_LABEL[item.kind],
          tone: item.tone,
        });
      }
    }
    return flags.sort((a, b) => (a.tone === b.tone ? 0 : a.tone === "danger" ? -1 : 1));
  }

  /** Sort weight: SOS above danger above warning above nothing. */
  function urgencyOf(group: Group): number {
    const flags = flagsOf(group);
    if (flags.some((flag) => flag.id === "sos")) return 3;
    if (flags.some((flag) => flag.tone === "danger")) return 2;
    return flags.length > 0 ? 1 : 0;
  }
  const columns: ColumnDef<Group, unknown>[] = [
    {
      id: "ref",
      accessorFn: (group) => group[0].ref,
      header: "Booking",
      cell: ({ row }) => (
        <AdminTableIdentity
          title={row.original[0].ref}
          subtitle={
            row.original.length > 1
              ? `Group · ${row.original.length} clients`
              : (row.original[0].client?.full_name ?? "Client unavailable")
          }
          fallback="BK"
        />
      ),
    },
    {
      id: "client",
      accessorFn: (group) => group.map((booking) => booking.client?.full_name ?? "").join(", "),
      header: "Customer",
      cell: ({ row }) => namesOf(row.original.map((booking) => booking.client?.full_name)),
    },
    {
      id: "therapist",
      accessorFn: (group) => namesOf(group.map((booking) => booking.therapist?.full_name)),
      header: "Therapist",
    },
    {
      id: "rider",
      accessorFn: (group) => namesOf(group.map((booking) => booking.rider?.full_name)),
      header: "Rider",
    },
    { id: "hub", accessorFn: (group) => group[0].hubs?.name ?? "", header: "Hub" },
    {
      id: "service",
      accessorFn: (group) =>
        group.map((booking) => booking.service_variants?.services?.name ?? "").join(", "),
      header: "Service",
      cell: ({ row }) =>
        row.original.map((booking) => booking.service_variants?.services?.name ?? "—").join(", "),
    },
    {
      id: "scheduled",
      accessorFn: (group) => group[0].scheduled_at,
      header: "Scheduled",
      cell: ({ row }) => {
        const status = statusOf(row.original);
        const overdue =
          (PRE_SESSION_STATUSES as readonly string[]).includes(status) &&
          minutesSince(row.original[0].scheduled_at) > 0;
        return (
          <span
            className={cn(
              "text-muted-foreground",
              overdue ? "font-semibold text-destructive" : null,
            )}
          >
            {new Date(row.original[0].scheduled_at).toLocaleString("en-PH", {
              dateStyle: "medium",
              timeStyle: "short",
            })}
            {overdue ? ` · ${minutesSince(row.original[0].scheduled_at)}m late` : ""}
          </span>
        );
      },
    },
    {
      id: "due",
      accessorFn: (group) => group.reduce((sum, booking) => sum + booking.amount_due, 0),
      header: "Due",
      cell: ({ row }) =>
        `₱${row.original.reduce((sum, booking) => sum + booking.amount_due, 0).toLocaleString("en-PH", { minimumFractionDigits: 2 })}`,
    },
    {
      id: "status",
      accessorFn: statusOf,
      header: "Status",
      cell: ({ row }) => (
        <Badge variant="outline" className="capitalize">
          {statusOf(row.original).replaceAll("_", " ")}
        </Badge>
      ),
    },
    ...(showAttention
      ? [
          {
            id: "attention",
            accessorFn: urgencyOf,
            header: "Attention",
            sortDescFirst: true,
            cell: ({ row }) => {
              const flags = flagsOf(row.original);
              return flags.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {flags.map((flag) => (
                    <Badge
                      key={flag.id}
                      variant={flag.tone === "danger" ? "destructive-soft" : "warning-soft"}
                    >
                      {flag.label}
                    </Badge>
                  ))}
                </div>
              ) : null;
            },
          } satisfies ColumnDef<Group, unknown>,
        ]
      : []),
    {
      id: "actions",
      header: "",
      enableSorting: false,
      cell: ({ row }) => (
        <AdminTableRowMenu
          label={row.original[0].ref}
          actions={[
            {
              label: "Copy booking ref",
              onSelect: () => void navigator.clipboard.writeText(row.original[0].ref),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <AdminDataTable
      presentation="standalone"
      data={groups}
      columns={columns}
      getRowId={(group) => group[0].booking_group_id ?? group[0].id}
      searchText={(group) =>
        group
          .map((booking) =>
            [
              booking.ref,
              booking.client?.full_name,
              booking.therapist?.full_name,
              booking.rider?.full_name,
              booking.hubs?.name,
              booking.service_variants?.services?.name,
              booking.status,
            ].join(" "),
          )
          .join(" ")
      }
      searchPlaceholder="Search ref, customer, staff, hub, or service…"
      facets={[
        ...(showAttention
          ? [
              {
                id: "attention",
                label: "Attention",
                options: [
                  { value: "needs_action", label: "Needs action" },
                  { value: "clear", label: "Nothing flagged" },
                ],
                getValue: (group: Group) => (urgencyOf(group) > 0 ? "needs_action" : "clear"),
              },
            ]
          : []),
        {
          id: "status",
          label: "Status",
          options: [
            "pending",
            "awaiting_rider",
            "dispatched",
            "en_route_to_client",
            "arrived",
            "in_session",
            "completed",
            "cancelled",
          ].map((status) => ({ value: status, label: status.replaceAll("_", " ") })),
          getValue: statusOf,
        },
      ]}
      initialSorting={[{ id: "scheduled", desc: true }]}
      loading={isLoading}
      emptyTitle="No matching bookings"
      emptyDescription={emptyDescription}
      exportFilename={exportFilename}
      exportScopeLabel="current authorized command feed"
      exportColumns={[
        { header: "Ref", value: (group) => group[0].ref },
        {
          header: "Customers",
          value: (group) => namesOf(group.map((booking) => booking.client?.full_name)),
        },
        {
          header: "Therapists",
          value: (group) => namesOf(group.map((booking) => booking.therapist?.full_name)),
        },
        {
          header: "Riders",
          value: (group) => namesOf(group.map((booking) => booking.rider?.full_name)),
        },
        { header: "Hub", value: (group) => group[0].hubs?.name },
        { header: "Scheduled", value: (group) => group[0].scheduled_at },
        {
          header: "Amount due",
          value: (group) => group.reduce((sum, booking) => sum + booking.amount_due, 0),
        },
        { header: "Status", value: statusOf },
        ...(showAttention
          ? [
              {
                header: "Attention",
                value: (group: Group) =>
                  flagsOf(group)
                    .map((flag) => flag.label)
                    .join("; "),
              },
            ]
          : []),
      ]}
    />
  );
}
